import React from 'react';
import Moment from 'moment';
import PropTypes from 'prop-types';
import AutoScopeStylesDecorator from '../../../Common/Utilities/Decorators/AutoScopeStylesDecorator';
import DataTableView from '../../../Common/Components/DataTableView/DataTableView.jsx';
import applyDecoratorsDoNotUseInNewCode from '../../../Common/Utilities/Decorators/ApplyDecorators';

class RateCardTable extends React.PureComponent {
    state = {
        currentSort: undefined
    }

    //This isn't very pretty, but we only know the visible rows after the
    //table component has applied its sorting and paging, so I save
    //them for later use.
    visibleRows = undefined;

    render() {
        const RowSelectionBox = ({ rowIndex, isSelected }) => <input onChange={ this.onCheckboxChanged } className="ui checkbox" type="checkbox" data-row-index={ rowIndex } checked={ isSelected } />

        const HeaderSelectionBox = () => {
            const { isSelected, isIndeterminate } = this.getSelectionStatus();

            //HACK: React doesn't support indeterminate out of the box due to its quirkiness, hence the `ref`: https://github.com/facebook/react/issues/1798
            //There may be better approaches, but I couldn't find an obvious alternative component or implementation to use.
            return <input type="checkbox" onChange={ this.onSelectAllCheckboxChanged } className="ui checkbox" ref={elem => elem && (elem.indeterminate = isIndeterminate)} checked={ isSelected } />
        }

        const formatDate = (date) => date ? Moment(date).format('YYYY-MM-DD') : "";
        const columns = [
            { Header: 'Rate card type', accessor: v => v.rateCardType.name, id: 'rateCardType', width: 90 },
            { Header: 'Charge type', accessor: v => v.chargeType.name, id: 'chargeType' },
            { Header: 'Customer', accessor: v => v.customer.name, id: 'customer' },
            { Header: 'Supplier', accessor: v => v.supplier.name, id: 'supplier' },
            { Header: 'From zone', accessor: v => v.fromZone.name, id: 'fromZone' },
            { Header: 'To zone', accessor: v => v.toZone.name, id: 'toZone' },
            { Header: 'Rate product', accessor: v => v.consignmentRateProduct.name, id: 'consignmentRateProduct' },
            { Header: 'Effective from', accessor: v => formatDate(v.effectivePeriod.effectiveFromInclusive), id: 'effectiveFrom', width: 90 },
            { Header: 'Effective to', accessor: v => formatDate(v.effectivePeriod.effectiveToInclusive), id: 'effectiveTo', width: 90 },
        ];

        const columnSort = this.state.currentSort === undefined
            ? columns.map(column => ({ desc: false, id: column.id }))
            : this.state.currentSort;

        if(this.props.onSelectionChanged) {
            const selectionColumn = { Header: column => <HeaderSelectionBox />, accessor: v => v.isSelectedInUi, id: 'bulkSelectionBox', resizable: false, sortable: false, width: 30, Cell: row => <RowSelectionBox rowIndex={ row.index } isSelected={ !!row.original.isSelectedInUi } /> };
            columns.unshift(selectionColumn);
        }

        return <div>
            <DataTableView
                data={ this.props.rateCards }
                columns={ columns }
                showPagination={ this.props.showPagination != false }
                showPageSizeOptions={ true }
                showPageJump={ true }
                sortable={ true }
                minRows={ 1 }
                expanded={ this.props.expanded }
                sorted={ columnSort }
                SubComponent={ row => <RateCardDetailRow row={ row } /> }
                onSortedChange={ this.onSortChange }
            >
                {(state, makeTable, instance) => {
                    function getVisibleRows(state) {
                        const startRow = state.pageSize * state.page;
                        const endRow = startRow + state.pageSize;
                        return state.sortedData.slice(startRow, endRow);
                    }

                    this.visibleRows = getVisibleRows(state);

                    return <div>{makeTable()}</div>;
                }}
            </DataTableView>
        </div>
    }

    onSortChange = (newSort) => {
        this.setState({ currentSort: newSort });
    }

    getSelectionStatus() {
        const visibleRows = this.visibleRows;
        const numberSelected = visibleRows.filter(r => r._original.isSelectedInUi).length;
        return {
            isSelected: visibleRows.length !== 0 && numberSelected === visibleRows.length,
            isIndeterminate: numberSelected !== visibleRows.length && numberSelected !== 0
        };
    }

    onSelectAllCheckboxChanged = event => {
        const shouldBeSelected = !this.getSelectionStatus().isSelected;
        for(const visibleRow of this.visibleRows)
            this.notifySelectionChanged(visibleRow._index, shouldBeSelected);
    }

    onCheckboxChanged = event => {
        const rateCardIndex = event.target.getAttribute('data-row-index');
        this.notifySelectionChanged(rateCardIndex, event.target.checked);
    }

    notifySelectionChanged(rateCardIndex, isSelected) {
        if(!this.props.onSelectionChanged)
            return;

        this.props.onSelectionChanged({ rateCardIndex, isSelected });
    }

    static propTypes = {
        rateCards: PropTypes.array.isRequired,
        showPagination: PropTypes.bool,
        expanded: PropTypes.object,        //for format, see documentation on `expanded` here: https://react-table.js.org/#/story/readme
        onSelectionChanged: PropTypes.func // onSelectionChanged({ rateCardIndex, isSelected }) If not provided, the column of selection boxes won't be shown.
    };
};

const RateCardDetailRow = ({row}) => {
    const rateCard = row.original;
    return <div>
        <RateCardDetailBlock rateCard={ rateCard } />
        { rateCard.rateTierSets.map((rts, index) => <RateCardTierSet key={ index } tierSet={ rts } />) }
    </div>
}

const RateCardDetailBlock = ({rateCard}) => {
    const arbitrarilyChosenTier = rateCard.rateTierSets[0].tiers[0];
    const renderDetail = (name, value) => (
        <div className="tier-detail">
            <div>{name + ':'}</div>
            <div>{ value }</div>
        </div>
    )
    return <div className="tier-details">
            { renderDetail("Currency", rateCard.currencyCode) }
            { renderDetail("Minimum", arbitrarilyChosenTier.minimum) }
            { renderDetail("Maximum", arbitrarilyChosenTier.maximum) }
            { renderDetail("Sold to", rateCard.soldTo ? rateCard.soldTo.name : null) }
            { renderDetail("Order line rate product", rateCard.orderLineRateProduct ? rateCard.orderLineRateProduct.name : null) }
            { renderDetail("Carrier", rateCard.carrier ? rateCard.carrier.name : null) }
        </div>;
}

const RateCardTierSet = ({tierSet}) => (
    <div className="tier-set">
        <div className="tier-set-details">
            <div className="tier-set-detail">
                <div>Charge per </div>
                <div>{ `${tierSet.chargePerQuantity} ${tierSet.unitOfMeasure.legacyDescription}` }</div>
            </div>
        </div>
        <table>
            <thead>
                <tr>
                    <th>From (inclusive)</th>
                    <th>To (exclusive)</th>
                    <th>Rate</th>
                </tr>
            </thead>
            <tbody>
                {
                    tierSet.tiers.slice().sort((t1, t2) => t1.tierFromInclusive - t2.tierFromInclusive).map((t, i) => (
                        <tr key={ i }>
                            <td>{ t.tierFromInclusive }</td>
                            <td>{ t.tierToExclusive }</td>
                            <td>{ '$' + t.tierRate }</td>
                        </tr>
                    ))
                }
            </tbody>
        </table>
    </div>
)

const withDecorators = applyDecoratorsDoNotUseInNewCode(AutoScopeStylesDecorator);

export default withDecorators(RateCardTable);
